import React, { useEffect, useState, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import SubscriptionService from "./SubscriptionService"
import routesfe from "../../Config/RoutesFE"
import Logger from "../../utils/Logger"
import { Auth } from 'aws-amplify'
import configDetails from "../../Config/Config"
import { clearAllPaymentStorage } from "../../utils/PaymentStorageUtils"
import "../../Styles/PayPalSuccess.css"
import {
  NoteForgeLogoSVG,
  PayPalIcon,
  PayPalLogo,
  AnimatedBackground,
  GlassCard,
  Button
} from "./PayPalSuccessComponents"
import noteforgelogo from "../../Landing/Images/noteforgelogo.jpg"

// ==================== MAIN COMPONENT ====================
const PayPalSuccess = ({ onUserUpdate }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [paymentData, setPaymentData] = useState(null)
  const totalTimeout = configDetails.paypalPolling?.timeout / 1000 || 300
  
  const getInitialRemainingTime = () => {
    const storedStartTime = sessionStorage.getItem('paypal_verification_start_time')
    if (storedStartTime) {
      const elapsed = Math.floor((Date.now() - parseInt(storedStartTime)) / 1000)
      const remaining = totalTimeout - elapsed
      return remaining > 0 ? remaining : 0
    }
    return totalTimeout
  }
  
  const [remainingTime, setRemainingTime] = useState(getInitialRemainingTime)
  const [, setPollingMessage] = useState("Processing your payment...")
  const [isProcessing, setIsProcessing] = useState(false)
  
  const getInitialRedirectCountdown = () => {
    const storedStartTime = sessionStorage.getItem('paypal_redirect_start_time')
    if (storedStartTime) {
      const elapsed = Math.floor((Date.now() - parseInt(storedStartTime)) / 1000)
      const remaining = 30 - elapsed
      return remaining > 0 ? remaining : 0
    }
    return 30
  }
  
  const [redirectCountdown, setRedirectCountdown] = useState(getInitialRedirectCountdown)
  const location = useLocation()
  const navigate = useNavigate()
  const hasProcessed = useRef(false)
  const hasCleared = useRef(false)

  // Countdown timer
  useEffect(() => {
    if (!loading) return
    const interval = setInterval(() => {
      setRemainingTime(prev => (prev <= 0 ? 0 : prev - 1))
    }, 1000)
    return () => clearInterval(interval)
  }, [loading])

  // Clear payment storage when payment is successful
  useEffect(() => {
    if (paymentData && !hasCleared.current) {
      hasCleared.current = true
      Logger.info('🧹 Clearing payment storage after successful PayPal payment')
      clearAllPaymentStorage()
    }
  }, [paymentData])

  // Auto-redirect countdown
  useEffect(() => {
    if (loading || !paymentData) return
    if (!sessionStorage.getItem('paypal_redirect_start_time')) {
      sessionStorage.setItem('paypal_redirect_start_time', Date.now().toString())
    }
    const interval = setInterval(() => {
      setRedirectCountdown(prev => {
        if (prev <= 1) {
          sessionStorage.removeItem('paypal_redirect_start_time')
          navigate(routesfe.HOME)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [loading, paymentData, navigate])

  // Process payment
  useEffect(() => {
    const processPayment = async () => {
      if (hasProcessed.current) return
      hasProcessed.current = true
      
      try {
        const params = new URLSearchParams(location.search)
        const token = params.get("token")
        const payerId = params.get("PayerID")

        if (!token || !payerId) {
          throw new Error("Missing payment parameters. Please try again.")
        }

        const completedPayment = sessionStorage.getItem('paypal_completed_payment')
        if (completedPayment) {
          const savedPaymentData = JSON.parse(completedPayment)
          setPaymentData(savedPaymentData)
          setLoading(false)
          return
        }

        const storedData = localStorage.getItem('paypal_pending_order')
        if (!storedData) {
          Logger.info("No pending order found - redirecting to home")
          navigate(routesfe.HOME, { replace: true })
          return
        }
        const pendingOrder = storedData ? JSON.parse(storedData) : {}
        
        const storedToken = sessionStorage.getItem('paypal_verification_token')
        if (storedToken !== token) {
          sessionStorage.setItem('paypal_verification_token', token)
          sessionStorage.setItem('paypal_verification_start_time', Date.now().toString())
        } else if (!sessionStorage.getItem('paypal_verification_start_time')) {
          sessionStorage.setItem('paypal_verification_start_time', Date.now().toString())
        }
        
        setPollingMessage("Verifying payment...")
        
        const result = await SubscriptionService.pollPaymentStatus(
          token,
          {},
          (statusResponse, attempts, elapsedTime) => {
            setPollingMessage("Verifying payment...")
          }
        )

        if (result.isTimeout && result.isProcessing) {
          Logger.info("Payment still processing after timeout", { result })
          sessionStorage.removeItem('paypal_verification_start_time')
          sessionStorage.removeItem('paypal_verification_token')
          setIsProcessing(true)
          setPollingMessage(result.message)
          setLoading(false)
          return
        }

        const isPaymentComplete = result.success
        if (result.isFailed || !isPaymentComplete) {
          sessionStorage.removeItem('paypal_verification_start_time')
          sessionStorage.removeItem('paypal_verification_token')
          navigate(routesfe.PAYPAL_FAILURE, {
            replace: true,
            state: {
              error: result.message || `Payment status: ${result.status}. Please try again.`,
              status: result.status,
              orderNumber: result.orderNumber,
              plan: {
                name: pendingOrder.planName,
                price: result.amount,
                currencySymbol: pendingOrder.currencySymbol || '$'
              }
            }
          })
          return
        }

        localStorage.removeItem('paypal_pending_order')
        sessionStorage.removeItem('paypal_verification_start_time')
        sessionStorage.removeItem('paypal_verification_token')

        const successPaymentData = {
          orderId: result.orderId,
          orderNumber: result.orderNumber,
          payerId: payerId,
          status: result.status,
          amount: result.amount,
          currency: pendingOrder.currency || "USD",
          currencySymbol: pendingOrder.currencySymbol || "$",
          planName: pendingOrder.planName || "Premium Plan",
          paymentId: result.paypalOrderId || token
        }

        sessionStorage.setItem('paypal_completed_payment', JSON.stringify(successPaymentData))
        setPaymentData(successPaymentData)

        try {
          const user = await Auth.currentAuthenticatedUser()
          await Auth.updateUserAttributes(user, { 'custom:is_subscribed': '1' })
          const updatedUser = await Auth.currentAuthenticatedUser({ bypassCache: true })
          if (onUserUpdate) onUserUpdate(updatedUser)
        } catch (err) {
          Logger.error('Failed to update subscription status:', err)
        }

        setLoading(false)
      } catch (err) {
        Logger.error("Error processing PayPal payment", { error: err.message })
        setError(err.message || "Failed to complete PayPal payment")
        setLoading(false)
      }
    }

    processPayment()
  }, [location.search, navigate, onUserUpdate])

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // ==================== LOADING STATE ====================
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 relative bg-gradient-to-br from-violet-50 via-indigo-50/50 to-blue-50">
        <AnimatedBackground />
        
        {/* Decorative floating elements */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {/* Floating dots */}
          <div className="absolute top-20 right-20 w-3 h-3 bg-violet-300 rounded-full animate-pulse" style={{ animationDelay: '0s' }} />
          <div className="absolute top-40 right-40 w-2 h-2 bg-indigo-300 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
          <div className="absolute bottom-32 right-24 w-2.5 h-2.5 bg-blue-300 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-32 left-32 w-2 h-2 bg-purple-300 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }} />
          <div className="absolute bottom-40 left-20 w-3 h-3 bg-indigo-200 rounded-full animate-pulse" style={{ animationDelay: '0.7s' }} />
          <div className="absolute top-1/2 right-16 w-2 h-2 bg-violet-200 rounded-full animate-pulse" style={{ animationDelay: '1.2s' }} />
          
          {/* Decorative rings */}
          <div className="absolute top-24 left-1/4 w-16 h-16 border border-violet-200/40 rounded-full" />
          <div className="absolute bottom-24 right-1/4 w-20 h-20 border border-indigo-200/30 rounded-full" />
          <div className="absolute top-1/3 right-1/3 w-12 h-12 border border-blue-200/30 rounded-full" />
          
          {/* Subtle gradient orbs */}
          <div className="absolute top-16 right-1/4 w-32 h-32 bg-gradient-to-br from-violet-100/20 to-transparent rounded-full blur-2xl" />
          <div className="absolute bottom-20 left-1/4 w-40 h-40 bg-gradient-to-br from-indigo-100/20 to-transparent rounded-full blur-2xl" />
        </div>
        
        {/* NoteForge Header - Top Left */}
        <div className="fixed top-6 left-6 z-50">
          <div className="flex items-center gap-3">
            <img src={noteforgelogo} alt="NoteForge" className="w-12 h-12 object-contain" />
            <div className="flex flex-col">
              <span className="font-bold text-slate-900 text-lg tracking-wide">NOTEFORGE</span>
              <span className="text-xs text-purple-600 flex items-center gap-1">
                <span className="text-amber-500">✨</span>
                Capture. Structure. Succeed.
              </span>
            </div>
          </div>
        </div>
        
        {/* Main Card - Compact */}
        <div className="relative w-full max-w-md z-10">
          <GlassCard variant="elevated" className="px-6 py-5">
            <div className="text-center">
              {/* PayPal Logo with animated circle border */}
              <div className="flex justify-center mb-4">
                <div className="relative inline-flex items-center justify-center w-24 h-24">
                  {/* Animated arc circle */}
                  <svg className="absolute inset-0 w-full h-full animate-spin-slow" viewBox="0 0 96 96">
                    <circle
                      cx="48"
                      cy="48"
                      r="46"
                      fill="none"
                      stroke="#1e3a5f"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeDasharray="220 70"
                    />
                  </svg>
                  <PayPalIcon animate={true} size="lg" />
                </div>
              </div>
              
              {/* Title */}
              <h1 className="text-lg font-bold text-slate-900 mb-1 text-balance">
                Verifying your payment
              </h1>
              <p className="text-slate-500 text-sm mb-4">
                Please wait while we confirm your transaction
              </p>
              
              {/* Timer - No background */}
              <div className="inline-flex items-center gap-3 px-4 py-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#1e3a5f] rounded-full animate-pulse" />
                  <span className="text-[#1e3a5f] text-sm font-medium">Processing</span>
                </div>
                <div className="w-px h-4 bg-slate-200" />
                <span className="text-lg font-bold text-[#1e3a5f] tabular-nums tracking-wider">
                  {formatTime(remainingTime)}
                </span>
              </div>
              
              {/* Warning - No background, bold red text */}
              <div className="mt-4 flex items-center justify-center gap-2">
                <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-red-600 text-sm font-bold">
                  Please do not close or refresh this page
                </p>
              </div>
            </div>
          </GlassCard>
        </div>
      </div>
    )
  }

  // ==================== PROCESSING STATE ====================
  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 relative">
        <AnimatedBackground />
        
        <div className="relative w-full max-w-md z-10">
          <GlassCard variant="elevated" className="p-8">
            <div className="text-center">
              {/* Clock Icon */}
              <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-6">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              <h1 className="text-2xl font-bold text-slate-900 mb-3">Processing Your Payment</h1>
              <p className="text-slate-500 text-sm mb-6 leading-relaxed max-w-xs mx-auto">
                Your payment is being finalized. You'll receive a confirmation email once complete.
              </p>
              
              {/* Info Card */}
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 mb-6 text-left">
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-violet-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900 mb-1">What happens next?</p>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Your payment is secure. You can safely close this page - we'll send you a confirmation email once your subscription is active.
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <Button variant="primary" className="w-full" onClick={() => navigate(routesfe.HOME)}>
                  Go to Home
                </Button>
                <Button variant="secondary" className="w-full" onClick={() => window.location.reload()}>
                  Check Status Again
                </Button>
              </div>
            </div>
          </GlassCard>
        </div>
      </div>
    )
  }

  // ==================== ERROR STATE ====================
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 relative">
        <AnimatedBackground />
        
        {/* Dual Branding Header */}
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50">
          <GlassCard variant="subtle" className="px-6 py-3 flex items-center gap-6">
            <div className="flex items-center gap-2.5">
              <NoteForgeLogoSVG animate={true} size="sm" />
              <span className="font-bold text-slate-900 tracking-tight">NoteForge</span>
            </div>
            <div className="w-px h-6 bg-gradient-to-b from-violet-200 to-blue-200" />
            <PayPalLogo animate={true} />
          </GlassCard>
        </div>
        
        <div className="relative w-full max-w-md z-10">
          <GlassCard variant="elevated" className="p-8">
            <div className="text-center">
              {/* Warning Icon */}
              <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-50 rounded-2xl mb-6">
                <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Verification Pending</h1>
              <p className="text-slate-500 text-sm mb-6">{error}</p>
              
              <div className="space-y-3">
                <Button variant="primary" className="w-full" onClick={() => window.location.reload()}>
                  Refresh Page
                </Button>
                <Button variant="secondary" className="w-full" onClick={() => navigate(routesfe.APP_SUBSCRIPTION_HOME)}>
                  Return to Plans
                </Button>
              </div>
            </div>
          </GlassCard>
        </div>
      </div>
    )
  }

  // ==================== SUCCESS STATE ====================
  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden bg-gradient-to-br from-violet-50 via-indigo-50/50 to-blue-50">
      <AnimatedBackground />
      
      {/* Confetti */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2.5 h-2.5 rounded-sm"
            style={{
              left: `${10 + Math.random() * 80}%`,
              backgroundColor: ['#8b5cf6', '#6366f1', '#3b82f6', '#10b981', '#f59e0b'][Math.floor(Math.random() * 5)],
              animation: `confetti-fall ${6 + Math.random() * 4}s linear infinite`,
              animationDelay: `${Math.random() * 5}s`,
              transform: `rotate(${Math.random() * 360}deg)`,
              opacity: 0.8
            }}
          />
        ))}
      </div>

      {/* NoteForge Header - Top Left */}
      <div className="fixed top-6 left-6 z-50">
        <div className="flex items-center gap-3">
          <img src={noteforgelogo} alt="NoteForge" className="w-12 h-12 object-contain" />
          <div className="flex flex-col">
            <span className="font-bold text-slate-900 text-lg tracking-wide">NOTEFORGE</span>
            <span className="text-xs text-purple-600 flex items-center gap-1">
              <span className="text-amber-500">✨</span>
              Capture. Structure. Succeed.
            </span>
          </div>
        </div>
      </div>

      {/* Success Card - Compact */}
      <div className="relative w-full max-w-md z-10">
        <GlassCard variant="elevated" className="overflow-hidden">
          {/* Success Header - More compact with enhanced gradient */}
          <div className="bg-gradient-to-r from-violet-600 via-indigo-600 to-blue-600 px-5 py-4 text-center relative overflow-hidden">
            {/* Shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" style={{ animation: 'shimmer 3s infinite' }} />
            
            {/* Success Icon with celebrations */}
            <div className="relative z-10">
              <div className="relative inline-flex items-center justify-center w-14 h-14 mb-2">
                {/* Celebration sparkles */}
                <div className="absolute -top-2 -left-2 w-3 h-3 bg-yellow-300 rounded-full animate-ping" style={{ animationDuration: '1.5s' }} />
                <div className="absolute -top-1 -right-3 w-2 h-2 bg-emerald-300 rounded-full animate-ping" style={{ animationDuration: '1.8s', animationDelay: '0.3s' }} />
                <div className="absolute -bottom-1 -left-3 w-2 h-2 bg-blue-300 rounded-full animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }} />
                <div className="absolute -bottom-2 -right-2 w-3 h-3 bg-violet-300 rounded-full animate-ping" style={{ animationDuration: '1.7s', animationDelay: '0.2s' }} />
                
                {/* Burst lines */}
                <div className="absolute inset-0 flex items-center justify-center">
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-0.5 h-4 bg-gradient-to-t from-transparent to-white/60"
                      style={{
                        transform: `rotate(${i * 45}deg) translateY(-20px)`,
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: `${i * 0.1}s`
                      }}
                    />
                  ))}
                </div>
                
                {/* Main tick circle with zoom animation */}
                <div className="absolute inset-0 bg-white/20 rounded-full animate-ping" />
                <div className="relative inline-flex items-center justify-center w-12 h-12 bg-white rounded-full shadow-lg animate-zoom-pulse">
                  <svg className="w-6 h-6 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              
              <h1 className="text-lg font-bold text-white mb-0.5">Payment Successful!</h1>
              <p className="text-violet-100 text-xs">
                Welcome to <span className="font-semibold">{paymentData?.planName}</span>
              </p>
            </div>
          </div>

          {/* Content - More compact */}
          <div className="p-5">
            {/* Order Details - Compact */}
            <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-100 mb-3">
              <h3 className="text-[10px] font-bold text-slate-900 mb-2.5 flex items-center uppercase tracking-wider">
                <div className="w-4 h-4 bg-violet-100 rounded flex items-center justify-center mr-1.5">
                  <svg className="w-2.5 h-2.5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                Order Details
              </h3>
              
              <div className="space-y-0">
                <div className="flex justify-between items-center py-1.5 border-b border-slate-200/70">
                  <span className="text-slate-500 text-[11px]">Order Number</span>
                  <span className="text-slate-900 font-mono text-[11px] font-medium">{paymentData?.orderNumber}</span>
                </div>
                
                {paymentData?.amount && (
                  <div className="flex justify-between items-center py-1.5 border-b border-slate-200/70">
                    <span className="text-slate-500 text-[11px]">Amount Paid</span>
                    <span className="text-base font-bold text-emerald-600">
                      {paymentData?.currencySymbol}{paymentData?.amount}
                    </span>
                  </div>
                )}
                
                <div className="flex justify-between items-center py-1.5 border-b border-slate-200/70">
                  <span className="text-slate-500 text-[11px]">Payment ID</span>
                  <span className="text-slate-900 font-mono text-[11px] font-medium truncate max-w-[160px]">{paymentData?.paymentId}</span>
                </div>

                <div className="flex justify-between items-center py-1.5">
                  <span className="text-slate-500 text-[11px]">Payment Method</span>
                  <div className="flex items-center gap-1.5">
                    <PayPalIcon size="sm" animate={false} />
                    <span className="text-[11px] font-semibold text-slate-900">PayPal</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Success Message - Compact */}
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-emerald-800 font-semibold text-xs">We are activating your subscription!</p>
                  <p className="text-emerald-700 text-[10px]">You'll receive an email once activated.</p>
                </div>
              </div>
            </div>

            {/* Redirect Countdown - Styled */}
            <div className="text-center mb-3">
              <p className="text-slate-400 text-[11px]">
                Redirecting to home in <span className="font-bold text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">{redirectCountdown}s</span>
              </p>
            </div>

            {/* Action Button - Compact */}
            <Button
              variant="primary"
              size="sm"
              className="w-full group"
              onClick={async () => {
                // Clear payment storage
                clearAllPaymentStorage()
                
                try {
                  const updatedUser = await Auth.currentAuthenticatedUser({ bypassCache: true })
                  if (onUserUpdate) onUserUpdate(updatedUser)
                } catch (err) {
                  Logger.error('Failed to refresh user attributes:', err)
                }
                sessionStorage.removeItem('paypal_redirect_start_time')
                navigate(routesfe.HOME)
              }}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Go to Home
            </Button>
          </div>
        </GlassCard>

        {/* Footer - Compact */}
        <div className="mt-4 text-center">
          <p className="text-slate-400 text-xs">
            Need help?{' '}
            <a 
              href="mailto:support@noteforge.ai" 
              className="font-medium text-violet-500 hover:text-violet-600 transition-colors underline underline-offset-2"
            >
              Contact Us
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default PayPalSuccess
